import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleHelp,
  Info,
  Phone,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { trackConversion } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { contact } from "@/lib/site-data";
import { FormPrivacyNotice } from "./FormPrivacyFields";

const questions = [
  {
    label: "Jaka jest łączna kwota Twojego zadłużenia?",
    options: [
      "do 30 000 zł",
      "30 000 - 100 000 zł",
      "100 000 - 300 000 zł",
      "powyżej 300 000 zł",
    ],
  },
  {
    label: "Czy dajesz radę spłacać raty na bieżąco?",
    options: [
      "Tak, bez większego problemu",
      "Z trudem - biorę nowe pożyczki, żeby spłacać stare",
      "Nie - mam zaległości, windykację lub komornika",
    ],
  },
  {
    label: "Czy prowadzisz działalność gospodarczą?",
    options: [
      "Nie, nigdy nie prowadziłem/am",
      "Prowadziłem/am, ale jest już zamknięta",
      "Tak, prowadzę obecnie",
    ],
  },
  {
    label:
      "Czy jesteś właścicielem lub współwłaścicielem nieruchomości albo wartościowego majątku (np. mieszkanie, dom, działka, samochód)?",
    options: ["Nie", "Tak"],
  },
];

type ResultKind = "A" | "B";
type QualificationTestProps = {
  city?: string;
  conversationHref?: string;
  conversationLabel?: string;
};

function resultFor(answers: string[]): ResultKind {
  const debtAllowsConversation = answers[0] !== "do 30 000 zł";
  const repaymentIsDifficult =
    answers[1] === "Z trudem - biorę nowe pożyczki, żeby spłacać stare" ||
    answers[1] === "Nie - mam zaległości, windykację lub komornika";
  return debtAllowsConversation && repaymentIsDifficult ? "A" : "B";
}

function answersNote(answers: string[]) {
  return [
    `Test: zadłużenie: ${answers[0]}`,
    `spłata rat: ${answers[1]}`,
    `działalność: ${answers[2]}`,
    `majątek: ${answers[3]}`,
  ].join("; ");
}

function CallbackForm({ answers, city }: { answers: string[]; city?: string }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [message, setMessage] = useState("");
  const submit = trpc.lead.submit.useMutation({
    onSuccess: () => {
      trackConversion("contact_form_submit", {
        form: "test",
        page: window.location.pathname,
      });
      setMessage("Dziękujemy. Ekspert skontaktuje się z Tobą.");
    },
    onError: () =>
      setMessage(
        "Nie udało się teraz przyjąć zgłoszenia. Zadzwoń: 513 513 613."
      ),
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || phone.replace(/\D/g, "").length < 7 || !accepted) {
      setMessage(
        "Podaj imię, telefon i potwierdź zapoznanie się z informacją o danych."
      );
      return;
    }
    setMessage("");
    submit.mutate({
      kind: "contact",
      name: name.trim(),
      phone,
      city: city || "ogolna",
      sourcePage: window.location.pathname,
      preferredChannel: "telefon",
      debtRange: answers[0],
      note: answersNote(answers),
      marketingPhone: false,
      marketingElectronic: false,
    });
  };

  return (
    <form className="test-callback-form" onSubmit={handleSubmit} noValidate>
      <label>
        Imię
        <input
          value={name}
          onChange={event => setName(event.target.value)}
          autoComplete="given-name"
        />
      </label>
      <label>
        Telefon
        <input
          value={phone}
          onChange={event => setPhone(event.target.value)}
          autoComplete="tel"
          inputMode="tel"
        />
      </label>
      <label className="test-callback-consent">
        <input
          type="checkbox"
          checked={accepted}
          onChange={event => setAccepted(event.target.checked)}
        />
        <span>Zapoznałem/am się z polityką prywatności.</span>
      </label>
      <FormPrivacyNotice />
      {message && <p className="form-error">{message}</p>}
      <button
        className="button-primary"
        type="submit"
        disabled={submit.isPending}
      >
        {submit.isPending ? "Wysyłanie..." : "Poproś o oddzwonienie"}
      </button>
    </form>
  );
}

function Result({
  kind,
  answers,
  restart,
  city,
}: {
  kind: ResultKind;
  answers: string[];
  restart: () => void;
  city?: string;
}) {
  const [showCallback, setShowCallback] = useState(false);
  const currentBusiness = answers[2] === "Tak, prowadzę obecnie";
  const hasAssets = answers[3] === "Tak";
  const details =
    kind === "A"
      ? {
          icon: CheckCircle2,
          className: "result-positive",
          title:
            "Kwalifikujesz się do rozmowy o umorzeniu długów poprzez upadłość",
          text: "Twoje odpowiedzi pokazują trwałą sytuację zadłużenia - a właśnie w takich sytuacjach upadłość konsumencka pozwala uwolnić się od długów. Ostateczną ocenę damy po bezpłatnej analizie informacji od Ciebie.",
          next: "Zrób następny krok i porozmawiaj z Ekspertem!",
        }
      : {
          icon: TriangleAlert,
          className: "result-caution",
          title: "Twoja sytuacja wymaga indywidualnej oceny",
          text: "Z Twoich odpowiedzi nie wynika jednoznacznie, czy upadłość konsumencka to dla Ciebie najlepsza droga. Ekspert w krótkiej, bezpłatnej rozmowie powie Ci, jakie masz możliwości.",
          next: "",
        };
  const Icon = details.icon;

  return (
    <div className={`test-result ${details.className}`} aria-live="polite">
      <Icon size={38} strokeWidth={1.6} />
      <p className="eyebrow">Twój następny krok</p>
      <h3>{details.title}</h3>
      <p>{details.text}</p>
      {details.next && (
        <div className="result-next">
          <ShieldCheck size={20} />
          <span>{details.next}</span>
        </div>
      )}
      {currentBusiness && (
        <p className="test-result-note">
          Przy aktywnej działalności gospodarczej sprawa wygląda inaczej -
          Ekspert powie Ci, jakie masz opcje.
        </p>
      )}
      {hasAssets && (
        <p className="test-result-note">
          Posiadanie mieszkania lub innego majątku nie wyklucza upadłości, ale
          ma wpływ na przebieg sprawy. Omówimy to podczas rozmowy.
        </p>
      )}
      <div className="result-actions">
        <a className="button-primary" href={contact.phoneHref}>
          <Phone size={18} /> Zadzwoń: {contact.phoneDisplay}
        </a>
        <button
          className="button-secondary"
          type="button"
          onClick={() => setShowCallback(current => !current)}
        >
          Chcę, żeby Ekspert do mnie oddzwonił
        </button>
        <button className="quiet-button" type="button" onClick={restart}>
          Wypełnij test ponownie
        </button>
      </div>
      {showCallback && <CallbackForm answers={answers} city={city} />}
      <p className="test-legal-disclaimer">
        <Info size={16} /> Wynik ma charakter orientacyjny, nie stanowi porady
        prawnej, a o ogłoszeniu upadłości rozstrzyga sąd.
      </p>
    </div>
  );
}

export function QualificationTest({ city }: QualificationTestProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<string[]>(
    Array(questions.length).fill("")
  );
  const [complete, setComplete] = useState(false);
  const question = questions[step];
  const choose = (value: string) =>
    setAnswers(current =>
      current.map((answer, index) => (index === step ? value : answer))
    );
  const reset = () => {
    setStep(0);
    setAnswers(Array(questions.length).fill(""));
    setComplete(false);
  };
  const showResult = () => {
    const result = resultFor(answers);
    setComplete(true);
    trackConversion("test_complete", {
      result,
      city: city ?? "ogolna",
      page: window.location.pathname,
    });
  };

  return (
    <section
      id="test"
      className="test-section section-pad"
      aria-labelledby="test-title"
    >
      <div className="shell-narrow">
        <div className="section-lead center-lead">
          <span className="section-kicker">01 · dyskretnie i bez danych</span>
          <h2 id="test-title">
            Sprawdź w 2 minuty, czy możesz umorzyć swoje długi
          </h2>
          <p>
            Odpowiedz na 4 krótkie pytania. Test jest anonimowy - nie prosimy o
            dane, aby pokazać wynik.
          </p>
        </div>
        <div className="test-card">
          {complete ? (
            <Result
              kind={resultFor(answers)}
              answers={answers}
              restart={reset}
              city={city}
            />
          ) : (
            <>
              <p className="test-legal-disclaimer">
                <Info size={16} /> Wynik ma charakter orientacyjny, nie stanowi
                porady prawnej, a o ogłoszeniu upadłości rozstrzyga sąd.
              </p>
              <div className="test-topline">
                <span>
                  Pytanie {step + 1} z {questions.length}
                </span>
                <span>
                  {Math.round(((step + 1) / questions.length) * 100)}%
                </span>
              </div>
              <div className="progress-track" aria-hidden="true">
                <span
                  style={{ width: `${((step + 1) / questions.length) * 100}%` }}
                />
              </div>
              <div className="test-question">
                <CircleHelp size={24} />
                <h3>{question.label}</h3>
              </div>
              <div className="choice-grid">
                {question.options.map(option => (
                  <button
                    type="button"
                    key={option}
                    className={`choice-card ${answers[step] === option ? "selected" : ""}`}
                    onClick={() => choose(option)}
                  >
                    <span className="choice-dot" aria-hidden="true" />
                    {option}
                  </button>
                ))}
              </div>
              <div className="test-controls">
                <button
                  type="button"
                  className="back-button"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                >
                  <ArrowLeft size={18} /> Wróć
                </button>
                <button
                  type="button"
                  className="button-primary"
                  disabled={!answers[step]}
                  onClick={() =>
                    step === questions.length - 1
                      ? showResult()
                      : setStep(step + 1)
                  }
                >
                  {step === questions.length - 1 ? (
                    "Pokaż wynik"
                  ) : (
                    <>
                      Dalej <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
        <p className="privacy-note">
          <ShieldCheck size={16} /> Test działa tylko w Twojej przeglądarce. Nie
          zapisujemy odpowiedzi, dopóki nie poprosisz o oddzwonienie.
        </p>
      </div>
    </section>
  );
}
